"use client";

import Image from "next/image";
import Link from "next/link";
import * as React from "react";
import { Search } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { buildBlogPath } from "../services/blog-url";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

type BlogTag = {
  blogTagId: string;
  blogTagTreeId: string;
  tagTreeName: string;
  parentBlogTagId?: string | null;
  name: string;
  slug: string;
  sortOrder: number;
};

type BlogListItem = {
  blogContentId: string;
  title: string;
  excerpt?: string | null;
  coverImageUrl?: string | null;
  publishedAtUtc: string;
  tags: BlogTag[];
};

type BlogTagNode = {
  blogTagId: string;
  blogTagTreeId: string;
  parentBlogTagId?: string | null;
  name: string;
  slug: string;
  sortOrder: number;
  children: BlogTagNode[];
};

type BlogTagTree = {
  blogTagTreeId: string;
  name: string;
  slug: string;
  sortOrder: number;
  tags: BlogTagNode[];
};

function formatUtcDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return "";

  return new Intl.DateTimeFormat("en", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    timeZone: "UTC",
  }).format(date);
}

function flattenTagNodes(nodes: BlogTagNode[], out: BlogTagNode[] = []) {
  for (const node of nodes) {
    out.push(node);
    if (node.children?.length) flattenTagNodes(node.children, out);
  }
  return out;
}

function findTagNode(nodes: BlogTagNode[], tagSlug: string): BlogTagNode | null {
  for (const node of nodes) {
    if (node.slug === tagSlug) return node;
    if (node.children?.length) {
      const found = findTagNode(node.children, tagSlug);
      if (found) return found;
    }
  }
  return null;
}

function getDescendantTagIds(node: BlogTagNode): string[] {
  const ids: string[] = [node.blogTagId];
  for (const child of node.children ?? []) {
    ids.push(...getDescendantTagIds(child));
  }
  return ids;
}

function PaginationBar(props: {
  page: number;
  totalPages: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  const { page, totalPages, onPrev, onNext } = props;
  if (totalPages <= 1) return null;

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="text-sm text-stone-600">
        Page <span className="font-semibold text-stone-900">{page}</span> of{" "}
        <span className="font-semibold text-stone-900">{totalPages}</span>
      </div>

      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={onPrev} disabled={page <= 1}>
          Previous
        </Button>
        <Button variant="outline" size="sm" onClick={onNext} disabled={page >= totalPages}>
          Next
        </Button>
      </div>
    </div>
  );
}

function TaxonomyTree(props: {
  tagTrees: BlogTagTree[];
  selected?: { treeSlug: string; tagSlug: string } | null;
}) {
  const { tagTrees, selected } = props;
  if (!tagTrees?.length) return null;

  const sortedTrees = [...tagTrees].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  const renderNode = (treeSlug: string, node: BlogTagNode, depth: number) => (
    <div key={node.blogTagId} className={depth > 0 ? "mt-2" : ""}>
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={
            selected?.treeSlug === treeSlug && selected?.tagSlug === node.slug
              ? "text-sm font-semibold text-stone-950 underline"
              : "text-sm font-semibold text-stone-800"
          }
        >
          {node.name}
        </span>
        {node.children?.length ? (
          <span className="text-xs text-stone-500">({node.children.length})</span>
        ) : null}
      </div>

      {node.children?.length ? (
        <div className="ml-4 border-l border-stone-200 pl-4">
          {node.children
            .slice()
            .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
            .map((child) => renderNode(treeSlug, child, depth + 1))}
        </div>
      ) : null}
    </div>
  );

  return (
    <Card className="border-stone-200 bg-white">
      <div className="p-5 sm:p-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.22em] text-stone-500">
            Explore by tags
          </div>
          <div className="mt-1 text-sm text-stone-600">
            Use these tag trees to browse posts by taxonomy.
          </div>
        </div>

        <div className="mt-4">
          <Accordion type="single" collapsible className="w-full" defaultValue={selected?.treeSlug}>
            {sortedTrees.map((tree) => (
              <AccordionItem key={tree.blogTagTreeId} value={tree.slug}>
                <AccordionTrigger className="text-left">
                  <span className="text-base font-semibold text-stone-900">{tree.name}</span>
                  <span className="ml-2 text-xs text-stone-500">({flattenTagNodes(tree.tags ?? []).length})</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    {(tree.tags ?? [])
                      .slice()
                      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                      .map((node) => renderNode(tree.slug, node, 0))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </Card>
  );
}

export default function BlogsIndexClient(props: {
  initialBlogs: BlogListItem[];
  tagTrees: BlogTagTree[];
  staticBlogContentIds?: string[];
  initialSelectedTag?: { treeSlug: string; tagSlug: string } | null;
  containerClassName?: string;
  isLoading?: boolean;
  errorMessage?: string | null;
}) {
  const { initialBlogs, tagTrees, staticBlogContentIds, initialSelectedTag, containerClassName, isLoading, errorMessage } = props;

  const [draftSearch, setDraftSearch] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [page, setPage] = React.useState(1);
  const pageSize = 10;

  const selected = initialSelectedTag ?? null;

  const selectedTagIds = React.useMemo(() => {
    if (!selected) return null;
    const tree = (tagTrees ?? []).find((t) => t.slug === selected.treeSlug);
    if (!tree) return null;
    const node = findTagNode(tree.tags ?? [], selected.tagSlug);
    if (!node) return null;
    return new Set(getDescendantTagIds(node));
  }, [selected, tagTrees]);

  const filteredItems = React.useMemo(() => {
    const searchValue = search.trim().toLowerCase();
    return (initialBlogs ?? [])
      .filter((item) => {
        if (selectedTagIds && selectedTagIds.size > 0) {
          const hasTag = (item.tags ?? []).some((tag) => selectedTagIds.has(tag.blogTagId));
          if (!hasTag) return false;
        }

        if (!searchValue) return true;

        const inTitle = item.title?.toLowerCase().includes(searchValue);
        const inExcerpt = item.excerpt?.toLowerCase().includes(searchValue);
        const inTags = (item.tags ?? []).some((tag) => tag.name?.toLowerCase().includes(searchValue));
        return Boolean(inTitle || inExcerpt || inTags);
      })
      .sort((a, b) => new Date(b.publishedAtUtc).valueOf() - new Date(a.publishedAtUtc).valueOf());
  }, [initialBlogs, search, selectedTagIds]);

  React.useEffect(() => {
    setPage(1);
  }, [search, selectedTagIds]);

  const totalCount = filteredItems.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const pageItems = filteredItems.slice(startIndex, startIndex + pageSize);
  const staticBlogIds = React.useMemo(() => new Set(staticBlogContentIds ?? []), [staticBlogContentIds]);

  const onSubmitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSearch(draftSearch);
  };

  return (
    <div className={cn("mx-auto w-full max-w-5xl", containerClassName)}>
      <div className="mx-auto w-full max-w-3xl">
        <form
          onSubmit={onSubmitSearch}
          className="flex w-full flex-col gap-3 rounded-lg border border-stone-200 bg-white p-2 shadow-sm sm:flex-row sm:items-center"
        >
          <div className="relative min-w-0 flex-1">
            <Search
              className="pointer-events-none absolute left-4 top-1/2 size-5 -translate-y-1/2 text-stone-500"
              aria-hidden="true"
            />
            <Input
              value={draftSearch}
              onChange={(e) => setDraftSearch(e.target.value)}
              placeholder="Search blogs"
              className="h-12 border-0 bg-stone-50 pl-12 text-base shadow-none focus-visible:ring-2 focus-visible:ring-secondary/35"
              aria-label="Search blogs"
            />
          </div>
          <Button type="submit" variant="secondary" className="h-12 px-8 text-base font-semibold">
            Search
          </Button>
        </form>
      </div>

      <div className="mt-6">
        <TaxonomyTree tagTrees={tagTrees} selected={selected} />
      </div>

      <div className="mt-6 flex flex-col gap-4">
        <PaginationBar
          page={safePage}
          totalPages={totalPages}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
        />

        {isLoading ? (
          <div className="rounded-xl border border-stone-200 bg-white p-6 text-sm text-stone-600">
            Loading blogs...
          </div>
        ) : errorMessage ? (
          <div className="rounded-xl border border-stone-200 bg-white p-6 text-sm text-stone-600">
            {errorMessage}
          </div>
        ) : pageItems.length === 0 ? (
          <div className="rounded-xl border border-stone-200 bg-white p-6 text-sm text-stone-600">
            No blogs found.
          </div>
        ) : (
          <div className="grid gap-4">
            {pageItems.map((item) => {
              const blogHref = staticBlogIds.has(item.blogContentId)
                ? buildBlogPath(item)
                : `/blog?id=${encodeURIComponent(item.blogContentId)}`;

              return (
                <Card key={item.blogContentId} className="overflow-hidden border-stone-200 bg-white">
                  <div className="grid gap-4 p-5 sm:grid-cols-[180px_1fr] sm:items-start">
                    <Link
                      href={blogHref}
                      className="relative block aspect-[16/10] w-full overflow-hidden rounded-lg border border-stone-200 bg-stone-50 sm:aspect-[4/3]"
                      aria-label={item.title}
                    >
                      {item.coverImageUrl ? (
                        <Image
                          src={item.coverImageUrl}
                          alt={item.title}
                          fill
                          className="object-cover"
                          sizes="(max-width: 640px) 100vw, 180px"
                          unoptimized
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center text-xs text-stone-500">
                          No cover
                        </div>
                      )}
                    </Link>

                    <div className="min-w-0">
                      <div className="text-xs font-semibold uppercase tracking-[0.22em] text-stone-500">
                        {formatUtcDate(item.publishedAtUtc)}
                      </div>
                      <h2 className="mt-2 text-lg font-semibold leading-snug text-stone-950 sm:text-xl">
                        <Link href={blogHref} className="hover:text-secondary hover:underline">
                          {item.title}
                        </Link>
                      </h2>

                      {(item.tags ?? []).length ? (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {item.tags.slice(0, 6).map((tag) => (
                            <Badge key={tag.blogTagId} variant="muted" className="bg-stone-100 text-stone-700">
                              {tag.name}
                            </Badge>
                          ))}
                        </div>
                      ) : null}

                      {item.excerpt ? (
                        <p className="mt-3 line-clamp-3 text-sm leading-6 text-stone-600">{item.excerpt}</p>
                      ) : null}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        <PaginationBar
          page={safePage}
          totalPages={totalPages}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
        />
      </div>
    </div>
  );
}
